"use client";

// WO-SITE-REAL-002 · M3 — THE MARKETPLACE SHOWS COMPANIES, NOT A PRODUCT DUMP.
//
// FOUNDER, verbatim (T7, 2026-08-12): *"Marketplace is showing every product. It's only supposed to
// show the product category from each individual company. If Spicy Sports sells rentals… it shows the
// company and it shows snowboards or rentals. When you click on it, it opens up the company and shows
// you the products there."* And (T2): *"showing huge descriptions for different products being listed
// in boxes… listing every single product on the Booking Store is not appropriate… a lot of text in
// some and a little in others, and the list is massive."*
//
// WHAT THIS REPLACES. `MemberPriceBand` rendered EVERY sellable line as its own card: 60 of them on
// Whistler, in a grid that grew to the length of the copy in each. Both rulings were violated by the
// same surface, which is why one component answers both.
//
// THE MODEL IS HIS, NOT OURS — and that is the whole reason this is a restore rather than a redesign.
// `src/data/catalogue.json` was captured from live's own supplier pages and is already shaped
// `vendors: { <slug>: { name, location, initials, glowColor, variant, products[] } }` — company first,
// products inside. Five companies, sixty products, on Whistler. His T7 example, "Spicy Sports sells
// rentals", is literally one of the five keys.
//
// THE JOIN, and which source owns which fact:
//   · WHO the companies are, and how they present (initials, brand colour, location) — the catalogue.
//   · WHAT is sellable and WHAT IT COSTS — the LIVE store feed, per the founder's 2026-08-13 pricing
//     ruling. The catalogue's prices are never rendered. A price on an indexed page comes from the
//     same source live charges from, or it does not render.
// Verified before this was written: the live feed's five vendor names match the catalogue's five
// EXACTLY, with the same per-company line counts (9/18/8/5/20 = 60). No name is normalised, fuzzy-
// matched or slugified to make that join — a company the catalogue does not know still renders, from
// the live feed alone, with the neutral presentation. That is deliberate: the
// `list.find(...) || list[0]` fallback is the fabrication generator CLAUDE.md §11 registered, and a
// missing logo is not worth reaching for it.
//
// UNIFORM HEIGHT IS STRUCTURAL, NOT COSMETIC (T2). Every card is the same fixed height whatever the
// company's copy length, because the defect he described was the RAGGEDNESS. The category list is
// capped and overflows to "+n"; nothing wraps to an unbounded number of lines.
//
// THE SPONSORED SLOT (T2) is built as a real, marked position with a real data shape and NO ad-serving
// logic, which is exactly what he asked for: he intends to sell that placement. Empty, it renders as a
// clearly-labelled available slot rather than pretending to be a company.
//
// ⚠️ `mounted` IS LOAD-BEARING, for the reason AccountChip.tsx and the old band both record: the
// session hydrates from localStorage, so the server render and the first client render disagree by
// construction, and React silently discards a mismatched subtree. Signed-OUT is the server-rendered
// truth; only the STRIKE is client state.

import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { isSignedIn, useCartStore } from "@/lib/cart-store";
import CATALOGUE from "@/data/catalogue.json";

export type PriceRow = {
  id: string;
  name: string;
  cat: string;
  vendor: string;
  guest: number;
  member: number;
};

type CatalogueVendor = { name: string; location?: string; initials?: string; glowColor?: string; variant?: string };

/** The catalogue's vendors for a mountain, keyed by their EXACT live name. Exact match only, by the
 *  standing rule — an unmatched company renders neutrally rather than borrowing another's identity. */
function catalogueVendors(code: string): Map<string, CatalogueVendor> {
  const shape = (CATALOGUE as { exportShape?: Record<string, { vendors?: Record<string, CatalogueVendor> }> }).exportShape;
  const forMountain = shape?.[code]?.vendors ?? {};
  return new Map(Object.values(forMountain).map((v) => [v.name, v]));
}

const wrap: CSSProperties = { maxWidth: 1160, margin: "0 auto", padding: "0 24px" };

/** Fixed, so a company with a paragraph of categories is exactly as tall as one with a word. */
const CARD_HEIGHT = 196;
/** Categories shown before the overflow marker. */
const CATS_SHOWN = 3;

export default function MountainStoreBand({
  rows,
  mountainName,
  mountainCode,
}: {
  rows: PriceRow[];
  mountainName: string;
  mountainCode: string;
}) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => setMounted(true), []);
  const session = useCartStore((s) => s.session);
  const signedIn = mounted && isSignedIn(session);

  const companies = useMemo(() => {
    const cat = catalogueVendors(mountainCode);
    const byVendor = new Map<string, PriceRow[]>();
    for (const r of rows) {
      const key = (r.vendor || "").trim();
      if (!key) continue; // a line with no company cannot be filed under one. It is not invented.
      const list = byVendor.get(key);
      if (list) list.push(r);
      else byVendor.set(key, [r]);
    }
    return Array.from(byVendor.entries())
      .map(([name, list]) => {
        const cheapest = list.reduce((a, b) => (b.member < a.member ? b : a));
        return {
          name,
          rows: list,
          cats: Array.from(new Set(list.map((r) => r.cat).filter(Boolean))),
          fromMember: cheapest.member,
          fromGuest: cheapest.guest,
          meta: cat.get(name),
        };
      })
      .sort((a, b) => b.rows.length - a.rows.length);
  }, [rows, mountainCode]);

  // No company, no band — the same honest empty state the price band had. An info-only mountain
  // renders nothing here rather than an empty shelf.
  if (!companies.length) return null;

  const totalLines = rows.length;

  return (
    <section
      style={{ padding: "56px 0", borderTop: "1px solid rgba(10,31,68,0.10)" }}
      data-price-band={signedIn ? "member" : "guest"}
      data-store-band="companies"
    >
      <div style={wrap}>
        <h2 style={{ margin: "0 0 6px", fontSize: "clamp(26px, 3vw, 38px)", lineHeight: 1.05, textTransform: "uppercase", color: "var(--sg-navy, #0A1F44)", fontWeight: 400 }}>
          Who you can book with in {mountainName}
        </h2>
        <p style={{ margin: "0 0 22px", fontSize: 15, color: "#5B666E" }}>
          {companies.length} {companies.length === 1 ? "company" : "companies"} · {totalLines} bookable{" "}
          {totalLines === 1 ? "line" : "lines"}.{" "}
          {signedIn ? "Member pricing is applied to your account." : "SnowClub members pay the member price."}
        </p>

        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(272px, 1fr))", gap: 16 }}>
          {companies.map((c) => {
            const isOpen = open === c.name;
            const shown = c.cats.slice(0, CATS_SHOWN);
            const over = c.cats.length - shown.length;
            return (
              <li key={c.name} style={{ display: "flex", flexDirection: "column" }}>
                <button
                  type="button"
                  data-store-company={c.name}
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : c.name)}
                  style={{
                    height: CARD_HEIGHT,
                    width: "100%",
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    border: "1px solid rgba(10,31,68,0.12)",
                    borderRadius: 12,
                    padding: "16px",
                    background: "#fff",
                    cursor: "pointer",
                    font: "inherit",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      aria-hidden
                      style={{
                        width: 36, height: 36, flex: "none", borderRadius: 9,
                        display: "grid", placeItems: "center",
                        // The company's own brand colour where the catalogue knows it; the neutral
                        // navy where it does not. Never another company's.
                        background: c.meta?.glowColor ?? "#0A1F44",
                        color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: "0.04em",
                      }}
                    >
                      {c.meta?.initials ?? c.name.slice(0, 2).toUpperCase()}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span
                        style={{
                          display: "block", fontSize: 16, lineHeight: 1.25, color: "#0A1F44", fontWeight: 600,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                      >
                        {c.name}
                      </span>
                      {c.meta?.location ? (
                        <span style={{ display: "block", fontSize: 12, color: "#8A949B" }}>{c.meta.location}</span>
                      ) : null}
                    </span>
                  </span>

                  {/* THE CATEGORIES — his "it shows the company and it shows snowboards or rentals".
                      Capped, so the tallest card is the shortest card. */}
                  <span style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "10px 0 0", minHeight: 24 }}>
                    {shown.map((t) => (
                      <span
                        key={t}
                        style={{
                          fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase",
                          color: "#5B666E", border: "1px solid rgba(10,31,68,0.14)", borderRadius: 999, padding: "3px 9px",
                        }}
                      >
                        {t}
                      </span>
                    ))}
                    {over > 0 ? <span style={{ fontSize: 11, color: "#8A949B", alignSelf: "center" }}>+{over}</span> : null}
                  </span>

                  <span style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{ fontSize: 11, color: "#8A949B", textTransform: "uppercase", letterSpacing: "0.08em" }}>from</span>
                      {/* A struck guest price is a promise about money: it renders only where the two
                          numbers actually differ, which on Whistler is 2 of the 5 companies. */}
                      {c.fromMember < c.fromGuest ? (
                        <>
                          <span
                            data-guest-price
                            style={{
                              fontSize: signedIn ? 13 : 18,
                              color: signedIn ? "#8A949B" : "#0A1F44",
                              textDecorationLine: signedIn ? "line-through" : "none",
                            }}
                          >
                            ${c.fromGuest}
                          </span>
                          <span data-member-price style={{ fontSize: 18, fontWeight: 700, color: signedIn ? "var(--sg-color-avalanche-orange, #FC500E)" : "#0A1F44" }}>
                            ${c.fromMember}
                          </span>
                        </>
                      ) : (
                        <span data-single-price style={{ fontSize: 18, fontWeight: 700, color: "#0A1F44" }}>${c.fromGuest}</span>
                      )}
                    </span>
                    <span style={{ fontSize: 12, color: "#5B666E" }}>
                      {c.rows.length} {c.rows.length === 1 ? "product" : "products"} {isOpen ? "▴" : "▾"}
                    </span>
                  </span>
                </button>

                {/* "WHEN YOU CLICK ON IT, IT OPENS UP THE COMPANY AND SHOWS YOU THE PRODUCTS THERE."
                    In place, and deliberately not behind a URL: a company page exists on this build for
                    SOME operators and not others, and picking a near-match to give every card a link is
                    the fabrication class. The behaviour he described is delivered; the link follows
                    when a verified company-page map exists, and is carded rather than guessed. */}
                {isOpen ? (
                  <ul data-store-company-products={c.name} style={{ listStyle: "none", margin: "10px 0 0", padding: 12, border: "1px solid rgba(10,31,68,0.12)", borderRadius: 12, background: "#F8FAFB" }}>
                    {c.rows.map((r) => (
                      <li key={r.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", borderTop: "1px solid rgba(10,31,68,0.07)" }}>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 13.5, color: "#0A1F44", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                          <span style={{ display: "block", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8A949B" }}>{r.cat}</span>
                        </span>
                        <span style={{ flex: "none", display: "flex", alignItems: "baseline", gap: 8 }}>
                          {r.member < r.guest ? (
                            <>
                              <span data-guest-price style={{ fontSize: signedIn ? 12 : 15, color: signedIn ? "#8A949B" : "#0A1F44", textDecorationLine: signedIn ? "line-through" : "none" }}>${r.guest}</span>
                              <span data-member-price style={{ fontSize: 15, fontWeight: 700, color: signedIn ? "var(--sg-color-avalanche-orange, #FC500E)" : "#0A1F44" }}>${r.member}</span>
                            </>
                          ) : (
                            <span data-single-price style={{ fontSize: 15, fontWeight: 700, color: "#0A1F44" }}>${r.guest}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}

          {/* ── THE SPONSORED PLACEMENT (T2) ──────────────────────────────────────────────────────
              He intends to SELL this position to vendors at this point in the flow. So the slot is
              real: same footprint as a company card, its own marked data shape, and no ad-serving
              logic of any kind — nothing selects, rotates, targets or counts. Empty, it says it is
              empty rather than dressing up as a company, because a fake sponsor on a commercial
              surface is the fabrication rail pointing at the money. */}
          <li>
            <div
              data-sponsored-slot="mountain-store"
              data-sponsor=""
              style={{
                height: CARD_HEIGHT,
                display: "flex", flexDirection: "column", justifyContent: "space-between",
                border: "1px dashed rgba(10,31,68,0.28)", borderRadius: 12, padding: 16, background: "#FCFDFD",
              }}
            >
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: "#8A949B" }}>
                Sponsored placement
              </span>
              <span style={{ fontSize: 13.5, lineHeight: 1.35, color: "#5B666E" }}>
                A vendor placement sits here. No sponsor is booked for {mountainName} yet.
              </span>
              <span style={{ fontSize: 11, color: "#8A949B" }}>Slot reserved · not an advertisement</span>
            </div>
          </li>
        </ul>
      </div>
    </section>
  );
}
